<template>
  <form class="stock-notification-form" @submit.prevent="handleFormSubmit">
    <TextField
      :value="email"
      :validation="v$.email"
      type="email"
      autocomplete="email"
      placeholder="Email Address"
      aria-label="Email Address"
      @input="(value) => (email = value)"
    />
    <Button type="submit" width="full">
      {{ buttonText }}
    </Button>
  </form>
</template>

<script>
import Button from "@/components/ButtonV3.vue";

import useVuelidate from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import { mapGetters, mapState, mapActions } from "vuex";
import { klaviyoLists } from "@/config";
import TextField from "@/components/TextField.vue";
import { bisEvent } from "@/helpers/events.js";

export default {
  name: "StockNotificationForm",
  components: {
    Button,
    TextField,
  },
  props: {
    product: {
      type: Object,
      default: null,
    },
    variant: {
      type: Object,
      default: null,
    },
    isPreLaunch: {
      type: Boolean,
      default: null,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: "",
      success: false,
      submitted: false,
    };
  },
  computed: {
    ...mapState("product", ["selectedVariant", "quickLookSelectedVariant"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("product", { storeIsPreLaunch: "isPreLaunch" }),
    activeVariant() {
      return (
        this.variant || this.selectedVariant || this.quickLookSelectedVariant
      );
    },
    activeIsPreLaunch() {
      if (this.isPreLaunch != null) {
        return this.isPreLaunch;
      }

      return (
        this.product?.tags?.includes("pre-launch") || this.storeIsPreLaunch
      );
    },
    buttonText() {
      if (this.submitted && this.success) {
        return this.activeIsPreLaunch
          ? "COMING SOON - SUBSCRIBED FOR STOCK ALERT"
          : "Subscribed for stock alert";
      }

      return this.activeIsPreLaunch
        ? "COMING SOON - NOTIFY ME WHEN AVAILABLE"
        : "Notify me when available";
    },
  },
  methods: {
    ...mapActions("add-to-bag", ["showCompactStockNotif"]),
    async handleFormSubmit() {
      const currencycCode = this.currencyCode.toLowerCase();
      const klaviyo = klaviyoLists[currencycCode];

      this.v$.email.$touch();
      if (this.v$.email.$invalid || !this.activeVariant) {
        return;
      }

      const variantId = this.activeVariant.id.split("ProductVariant/")[1];

      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: this.email,
                },
              },
            },
            channels: ["EMAIL"],
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-05-15",
        },
        body: JSON.stringify(payload),
      };

      const bisResponse = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${klaviyo.accountId}`,
        requestOptions
      );

      if (bisResponse.status === 202) {
        this.success = true;
        this.submitted = true;

        bisEvent();

        setTimeout(() => {
          this.showCompactStockNotif({
            product: null,
            variant: null,
            show: false,
          });
        }, 3000);
      } else {
        this.success = false;
        this.submitted = true;
      }
    },
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage(
          "Please enter a valid Email Address",
          required
        ),
        email: helpers.withMessage("Please enter a valid Email Address", email),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.stock-notification-form {
  margin-bottom: 8px;

  .field {
    margin-bottom: 8px;
  }
}
</style>
